/**
 * Discover the language variants available for a given Questionnaire on the
 * client document viewer.
 *
 * Variants are sourced from the active Intake Configuration `PlanDefinition`:
 * each form row may carry one or more `INTAKE_ITEM_VARIANT_EXTENSION` entries
 * with `languageCode` siblings of the base template. We scan the active
 * configuration for any item whose `definitionCanonical` matches the current
 * Questionnaire URL **or** whose variants reference it, then return the
 * union of the base + variant entries.
 *
 * Returning an empty list is the signal to *hide* the language dropdown
 * entirely (no point in showing a chooser with one option). The caller is
 * responsible for swapping the active Questionnaire when the user picks a
 * different language.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Bundle, PlanDefinition } from '@medplum/fhirtypes';

import {
  INTAKE_CONFIG_CANONICAL_URL,
  INTAKE_ITEM_VARIANT_EXTENSION,
} from '@/lib/intake-configuration/types';

export interface DocumentLanguageVariant {
  /** BCP-47 language code, or `undefined` for the default language. */
  languageCode: string | undefined;
  /** Logical canonical URL of the variant Questionnaire. */
  templateUrl: string;
  /** Cached display title from the configuration (admin-set). */
  title: string;
  /** True when this is the base/default template (no language code). */
  isDefault: boolean;
}

interface UseDocumentLanguageVariantsResult {
  variants: DocumentLanguageVariant[];
  loading: boolean;
  error: Error | null;
}

const VARIANT_SUB_TEMPLATE_URL = 'templateUrl';
const VARIANT_SUB_TITLE = 'title';
const VARIANT_SUB_LANGUAGE = 'languageCode';
const VARIANT_SUB_ENABLED = 'enabled';

/**
 * Walk every PlanDefinition.action looking for items that reference the
 * given canonical URL — either as the base template (`definitionCanonical`)
 * or via a variant sub-extension. Returns one set of variants per matching
 * item; we union them and dedupe by `languageCode`.
 *
 * Exported for unit testing — production code should use the hook below.
 */
export function collectVariants(
  plan: PlanDefinition,
  baseUrl: string,
): DocumentLanguageVariant[] {
  const found: DocumentLanguageVariant[] = [];

  const walk = (actions?: PlanDefinition['action']): void => {
    for (const action of actions ?? []) {
      const baseTemplate = action.definitionCanonical;
      const variantExtensions =
        action.extension?.filter((e) => e.url === INTAKE_ITEM_VARIANT_EXTENSION) ?? [];

      const variantEntries = variantExtensions
        .map((ext) => {
          const sub = ext.extension ?? [];
          const enabled = sub.find((s) => s.url === VARIANT_SUB_ENABLED)?.valueBoolean ?? true;
          if (!enabled) return null;
          const url = sub.find((s) => s.url === VARIANT_SUB_TEMPLATE_URL)?.valueString;
          if (!url) return null;
          const languageCode = sub.find((s) => s.url === VARIANT_SUB_LANGUAGE)?.valueString;
          const title = sub.find((s) => s.url === VARIANT_SUB_TITLE)?.valueString ?? url;
          return { url, languageCode, title };
        })
        .filter((v): v is { url: string; languageCode: string | undefined; title: string } =>
          v !== null,
        );

      const matchesBase = baseTemplate === baseUrl;
      const matchesVariant = variantEntries.some((v) => v.url === baseUrl);
      if (matchesBase || matchesVariant) {
        if (baseTemplate) {
          found.push({
            languageCode: undefined,
            templateUrl: baseTemplate,
            title: action.title?.trim() || baseTemplate,
            isDefault: true,
          });
        }
        for (const v of variantEntries) {
          if (!v.languageCode) continue;
          found.push({
            languageCode: v.languageCode,
            templateUrl: v.url,
            title: v.title,
            isDefault: false,
          });
        }
      }

      walk(action.action);
    }
  };
  walk(plan.action);
  return found;
}

/** Exported for unit testing. First-occurrence wins per language code. */
export function dedupeByLanguage(entries: DocumentLanguageVariant[]): DocumentLanguageVariant[] {
  const byLang = new Map<string, DocumentLanguageVariant>();
  for (const entry of entries) {
    const key = entry.languageCode ?? '__default__';
    if (!byLang.has(key)) byLang.set(key, entry);
  }
  return [...byLang.values()];
}

export function useDocumentLanguageVariants(
  baseUrl: string | undefined,
): UseDocumentLanguageVariantsResult {
  const medplum = useMedplum();
  const [plans, setPlans] = useState<PlanDefinition[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!baseUrl) {
      setPlans([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const bundle: Bundle = await medplum.search('PlanDefinition', {
          url: INTAKE_CONFIG_CANONICAL_URL,
          status: 'active',
          _sort: '-_lastUpdated',
          _count: '1',
        });
        const found = (bundle.entry ?? [])
          .map((e) => e.resource as PlanDefinition | undefined)
          .filter((p): p is PlanDefinition => Boolean(p));
        if (!cancelled) setPlans(found);
      } catch (err) {
        if (!cancelled) {
          setPlans([]);
          setError(err instanceof Error ? err : new Error('Failed to load language variants'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl, medplum]);

  const variants = useMemo(() => {
    if (!baseUrl || !plans) return [];
    const all = plans.flatMap((p) => collectVariants(p, baseUrl));
    const deduped = dedupeByLanguage(all);
    // Hide the selector when there's nothing to choose between (only the
    // base template available) — caller should branch on `length > 1`.
    return deduped.length > 1 ? deduped : [];
  }, [baseUrl, plans]);

  return { variants, loading, error };
}
